import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

/** Counts of items that still need staff attention, keyed by quick-link route. */
export type PendingCounts = Record<string, number>;

export function useAdminPendingCounts(enabled: boolean) {
  const [counts, setCounts] = useState<PendingCounts>({});
  const [loading, setLoading] = useState(enabled);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    (async () => {
      try {
        const [phase0, outreach, consistency, invites] = await Promise.all([
          supabase
            .from('phase0_submissions')
            .select('*', { count: 'exact', head: true })
            .eq('status', 'submitted'),
          supabase
            .from('outreach_log')
            .select('*', { count: 'exact', head: true })
            .in('status', ['contacted', 'in progress']),
          supabase
            .from('data_consistency_runs')
            .select('issue_count')
            .order('ran_at', { ascending: false })
            .limit(1)
            .maybeSingle(),
          supabase.rpc('get_pending_teammate_invites'),
        ]);
        if (cancelled) return;

        const inviteRows = Array.isArray(invites.data) ? invites.data : [];
        setCounts({
          '/admin/phase0-documents': phase0.count ?? 0,
          '/admin/outreach-log': outreach.count ?? 0,
          '/admin/data-consistency': consistency.data?.issue_count ?? 0,
          '/admin/pending-teammate-invites': inviteRows.length,
        });
      } catch {
        if (!cancelled) setCounts({});
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { counts, loading };
}
